/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Claude Bridge provider
 *
 * Talks to the local Hivemind Claude Bridge (bridge/server.mjs), which routes
 * Claude requests through the Claude Agent SDK using your Claude Code login —
 * billing your Claude Pro/Max subscription instead of API credits.
 *
 * The bridge is auto-detected: when it's running on this machine, Claude
 * calls (without tools) go through it; otherwise the app falls back to the
 * direct Anthropic API with your API key.
 *
 * localStorage controls:
 *   hivemind_claude_bridge      = 'off' to disable auto-detection
 *   hivemind_claude_bridge_url  = override URL (default http://localhost:8787)
 */

import type { ChatCompletionOptions, ChatCompletionResponse } from './llm-provider'
import { useLogsStore } from '@/ui/stores/logs-store'

const DEFAULT_BRIDGE_URL = 'http://localhost:8787'

export function getBridgeUrl(): string {
  try {
    return localStorage.getItem('hivemind_claude_bridge_url') || DEFAULT_BRIDGE_URL
  } catch {
    return DEFAULT_BRIDGE_URL
  }
}

export function isBridgeEnabled(): boolean {
  try {
    return localStorage.getItem('hivemind_claude_bridge') !== 'off'
  } catch {
    return true
  }
}

// Cache availability so we don't health-check on every single call
let lastCheck = 0
let lastResult = false
const CHECK_TTL_MS = 30_000

export async function checkBridgeAvailable(force = false): Promise<boolean> {
  if (!isBridgeEnabled()) return false

  const now = Date.now()
  if (!force && now - lastCheck < CHECK_TTL_MS) return lastResult
  lastCheck = now

  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 1500)
    const res = await fetch(`${getBridgeUrl()}/health`, { signal: controller.signal })
    clearTimeout(timer)
    const data = await res.json()
    lastResult = data?.ok === true
  } catch {
    lastResult = false
  }

  if (import.meta.env.DEV) {
    console.log(`[Claude Bridge] Availability: ${lastResult ? 'online ✓ (subscription billing)' : 'offline'}`)
  }
  return lastResult
}

/**
 * Synchronous, cached view of bridge availability for code paths that can't
 * await (e.g. smart provider routing). Kicks off a background refresh so the
 * cache stays warm; may return false on the very first call after page load.
 */
export function isBridgeLikelyAvailable(): boolean {
  void checkBridgeAvailable()
  return isBridgeEnabled() && lastResult
}

/**
 * Chat completion via the local Claude bridge (subscription billing).
 * Tools are not supported — callers should route tool calls to the API instead.
 */
export async function chatCompletionClaudeBridge(
  options: ChatCompletionOptions
): Promise<ChatCompletionResponse> {
  const startTime = Date.now()
  const bridgeUrl = getBridgeUrl()

  useLogsStore.getState().addLog({
    type: 'request',
    provider: 'Claude Bridge (Max)',
    model: options.model,
    message: `Sending request to ${options.model} via local bridge (subscription billing)`,
  })

  const res = await fetch(`${bridgeUrl}/v1/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: options.model,
      messages: options.messages,
      stream: !!options.stream,
    }),
  })

  if (!res.ok) {
    let detail = ''
    try {
      detail = (await res.json())?.error || ''
    } catch { /* not json */ }
    throw new Error(`Claude bridge error (${res.status}): ${detail || res.statusText}`)
  }

  let content = ''
  let usage: ChatCompletionResponse['usage'] = undefined

  if (options.stream && res.body) {
    // NDJSON stream: {type:'chunk'|'done'|'error', ...} one per line
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const handleLine = (line: string) => {
      if (!line.trim()) return
      const event = JSON.parse(line)
      if (event.type === 'chunk' && event.text) {
        content += event.text
        options.onChunk?.(event.text)
      } else if (event.type === 'done') {
        content = event.content ?? content
        if (event.usage) {
          usage = {
            promptTokens: event.usage.promptTokens || 0,
            completionTokens: event.usage.completionTokens || 0,
            totalTokens: event.usage.totalTokens || 0,
            cost: 0, // subscription — no marginal cost
          }
        }
      } else if (event.type === 'error') {
        throw new Error(`Claude bridge error: ${event.message}`)
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      for (const line of lines) handleLine(line)
    }
    if (buffer.trim()) handleLine(buffer)
  } else {
    const data = await res.json()
    content = data.content || ''
    if (data.usage) {
      usage = {
        promptTokens: data.usage.promptTokens || 0,
        completionTokens: data.usage.completionTokens || 0,
        totalTokens: data.usage.totalTokens || 0,
        cost: 0,
      }
    }
  }

  const duration = Date.now() - startTime
  useLogsStore.getState().addLog({
    type: 'response',
    provider: 'Claude Bridge (Max)',
    model: options.model,
    message: `Received response (${usage?.totalTokens ?? '?'} tokens, $0 — subscription)`,
    details: {
      promptTokens: usage?.promptTokens,
      completionTokens: usage?.completionTokens,
      totalTokens: usage?.totalTokens,
      cost: 0,
      duration,
    },
  })

  return { content, usage }
}
